import moment from "moment";

const hoursInADay = 24 * 60 * 60 * 1000;

export const hoursInADayArray = Array.from({ length: 24 }, (_, i) =>
  String(i).padStart(2, "0"),
);

export const monthsInAYear = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const daysInAWeek = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
export const fullDaysInAWeek = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

export const formatDuration = (durationInMs: number) => {
  const days = Math.floor(durationInMs / hoursInADay);

  const utcDate = moment.utc(durationInMs % hoursInADay);

  const formattedHourMinSec = utcDate.format("H[hr] mm[min] ss[s]");

  const formattedHourMin = utcDate.format("H[hr] mm[min]");

  const formattedDay = days > 0 ? `${days}${days === 1 ? "day" : "days"} ` : "";

  return {
    duration: `${formattedDay}${formattedHourMinSec}`,
    durationNoSecs: `${formattedDay}${formattedHourMin}`,
    days,
    leftOverDurationFromDays: utcDate,
  };
};

export const getDateDifference = (startDate: Date, endDate: Date) => {
  // create two moment objects for the given dates
  const date1 = moment(startDate);
  const date2 = moment(endDate);

  // get the difference in milliseconds
  const diff = date2.diff(date1);

  const { durationNoSecs } = formatDuration(diff);

  return durationNoSecs;
};

export const getDaysInAMonth = (selectedDay: moment.Moment) => {
  const daysArray: moment.Moment[] = [];
  // eslint-disable-next-line no-plusplus
  for (let index = 0; index < selectedDay.daysInMonth(); index++) {
    const day = moment(selectedDay).startOf("month").add(index, "days");
    daysArray.push(day);
  }
  return daysArray;
};

export const generateDates = (selectedDate: moment.Moment) => {
  const startOfMonth = selectedDate.clone().startOf("month");
  const endOfMonth = selectedDate.clone().endOf("month");

  const startDate = startOfMonth.clone().startOf("week");
  const endDate = endOfMonth.clone().endOf("week");

  const dates: moment.Moment[] = [];

  const current = startDate.clone();

  while (current.isSameOrBefore(endDate)) {
    dates.push(current.clone());
    current.add(1, "day");
  }

  return dates;
};

export const dateIsWithinRange = (
  date: moment.Moment,
  minDate?: moment.Moment,
  maxDate?: moment.Moment,
) => {
  if (!minDate && !maxDate) {
    return true;
  }

  const minDateBool = minDate ? date.isSameOrAfter(minDate, "day") : true;

  const maxDateBool = maxDate ? date.isSameOrBefore(maxDate, "day") : true;

  return minDateBool && maxDateBool;
};

export const groupDataByDate = <T>(
  data?: T[],
  dateKey = "created_at",
  toLocalTime = false,
): { date: string; fullDate: string; data: T[] }[] => {
  if (!data) return [];

  const groupedData: Record<string, T[]> = {};

  data.forEach((dataItem) => {
    const date = toLocalTime
      ? // @ts-expect-error - eslint type error
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        moment.utc(dataItem[dateKey]).local().format("YYYY-MM-DD")
      : // @ts-expect-error - eslint type error
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        moment(dataItem[dateKey]).format("YYYY-MM-DD");
    if (!groupedData[date]) {
      groupedData[date] = [];
    }
    const newDataDate = toLocalTime
      ? // @ts-expect-error - eslint type error
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        moment.utc(dataItem[dateKey]).local().format()
      : // @ts-expect-error - eslint type error
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        moment(dataItem[dateKey]);

    groupedData[date].push({
      ...dataItem,
      [dateKey]: newDataDate,
    });
  });

  // Convert object to array of objects with date and data
  const result = Object.keys(groupedData).map((date) => ({
    date,
    // @ts-expect-error - eslint type error
    // eslint-disable-next-line
    fullDate: groupedData[date][0][dateKey],
    data: groupedData[date],
  }));

  return result;
};

export const getRollingTwelveMonthIsoRange = () => ({
  startDate: moment()
    .clone()
    .subtract(11, "month")
    .startOf("month")
    .toISOString(),
  endDate: moment().endOf("month").toISOString(),
});

export const getStartAndEndDate = (
  range: "day" | "week" | "month" | "year" | "last6months" | "all",
) => {
  if (range === "all" || range === "last6months") {
    return {
      startDate: undefined,
      endDate: undefined,
      formattedStartDate: "",
      formattedEndDate: "",
    };
  }

  if (range === "year") {
    const startDate = moment().clone().subtract(11, "month").startOf("month");
    const endDate = moment().clone().endOf("month");
    const formattedStartDate = startDate.format("Do MMM");
    const formattedEndDate = endDate.format("Do MMM");

    return {
      startDate,
      endDate,
      formattedStartDate,
      formattedEndDate,
    };
  }

  const today = moment();

  const startDate = today.clone().startOf(range);
  const endDate = today.clone().endOf(range);

  const formattedStartDate = startDate?.format("Do MMM");
  const formattedEndDate = endDate?.format("Do MMM");

  return {
    startDate,
    endDate,
    formattedStartDate,
    formattedEndDate,
  };
};

export const datesInMonth = (date?: moment.Moment) => {
  const dateToUse = date ?? moment();

  const daysArray: string[] = [];

  // eslint-disable-next-line no-plusplus
  for (let index = 0; index < dateToUse.daysInMonth(); index++) {
    const day = moment(dateToUse)
      .startOf("month")
      .add(index, "days")
      .format("DD");
    daysArray.push(day);
  }
  return daysArray;
};

export const lastSixMonth = (date?: moment.Moment) => {
  const dateToUse = date ?? moment();
  const monthArray: string[] = [];

  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < 6; i++) {
    const monthDate = dateToUse.clone().subtract(i, "months");
    const monthName = monthDate.format("MMM");
    monthArray.unshift(monthName);
  }
  return monthArray;
};

export const lastTwelveMonths = (date?: moment.Moment) => {
  const dateToUse = date ?? moment();
  const monthArray: string[] = [];

  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < 12; i++) {
    const monthDate = dateToUse.clone().subtract(i, "months");
    const monthName = monthDate.format("MMM");
    monthArray.unshift(monthName);
  }
  return monthArray;
};

export const getDateIndex = (
  date?: string,
  array?: string[],
  formatter?: string,
) => {
  if (!date || !array || !formatter) return undefined;

  const formattedDate = moment(date).format(formatter);

  const foundDateIndex = array.findIndex((item) => item === formattedDate);

  return foundDateIndex === -1 ? undefined : foundDateIndex;
};

// filter data by date range
export const filterDataByDateRange = <T>(
  data?: T[],
  dateKey = "created_at",
  startDate?: moment.Moment,
  endDate?: moment.Moment,
) => {
  if (!data || data?.length <= 0) {
    return [];
  }
  return data.filter((item: T) => {
    // @ts-expect-error - eslint type error
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const date = moment(item[dateKey]);
    return dateIsWithinRange(date, startDate, endDate);
  });
};

export const getDOBDate = (date?: string) => {
  if (!date) {
    return undefined;
  }

  return moment(date).format("YYYY/MM/DD");
};

export const formatDateTime = (date?: Date) => {
  if (!date) return undefined;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export const getSecondsToHoursMinAndSecs = (seconds: number) => {
  const hour = Math.floor(seconds / 3600);
  const remainingSeconds = seconds % 3600;
  const min = Math.floor(remainingSeconds / 60);
  const sec = remainingSeconds % 60;

  const hourWithZeroPrefix = hour < 10 ? `0${hour}` : hour;
  const minWithZeroPrefix = min < 10 ? `0${min}` : min;
  const secWithZeroPrefix = sec < 10 ? `0${sec}` : sec;

  return {
    hour,
    min,
    sec,
    hourWithZeroPrefix,
    minWithZeroPrefix,
    secWithZeroPrefix,
  };
};

export const getMonthWeeks = (date: Date | null | undefined) => {
  const m = moment(date);
  const startOfMonth = m.clone().startOf("month");
  const endOfMonth = m.clone().endOf("month");

  const weeks = [];
  let currentStart = startOfMonth.clone().startOf("day");
  let weekIndex = 1;

  while (currentStart.isSameOrBefore(endOfMonth)) {
    const currentEnd = moment.min(
      currentStart.clone().add(6, "days").endOf("day"), // 7-day block
      endOfMonth,
    );

    weeks.push({
      label: `week ${weekIndex}`,
      start: currentStart.clone().toISOString(),
      end: currentEnd.clone().toISOString(),
    });

    // Move to next week
    currentStart = currentStart.clone().add(7, "days").startOf("day");
    weekIndex += 1;
  }

  return weeks;
};
